import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTickets } from "../../hooks/useTickets";
import { TicketPriority } from "../../service/CallCenterApi";
import {
  getFormColumns,
  PREDEFINED_CUSTOM_FIELD_KEYS,
  PREDEFINED_TICKET_FIELD_KEYS,
} from "../../components/call-center/ticketUploadUtils";
import { getAgentWorkspaceInit } from "../../service/CallCenterTickets";
import useCallCenterT from "../../hooks/useCallCenterT";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiList,
  FiSettings,
  FiStar,
  FiUpload,
} from "react-icons/fi";
import ConfirmDialog from "../../components/call-center/listing/ConfirmDialog";
import PhoneNumberFieldArray from "../../components/call-center/PhoneNumberFieldArray";
import {
  findDuplicatePhoneIndexes,
  hasValidMobileNumbers,
  joinMobileNumberList,
  validatePhoneNumber,
} from "../../utils/phoneNumberUtils";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_CARD_CLASS,
  LISTING_CONTAINER_CLASS,
  LISTING_FILTER_CARD_CLASS,
  LISTING_PAGE_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
} from "../../components/call-center/listing/listingStyles";

const FORM_FIELD_ORDER = [
  "accountNo",
  "mobileNumber",
  "priority",
  "assignedTo",
  "targetOutstanding",
  "targetPaymentDate",
  "remarks",
];

const SECTION_KEYS = {
  account: new Set(["accountNo"]),
  contact: new Set(["mobileNumber"]),
  assignment: new Set(["priority", "assignedTo"]),
  payment: new Set(["targetOutstanding", "targetPaymentDate"]),
  notes: new Set(["remarks"]),
};

const TEXTAREA_CLASS =
  "w-full min-h-[92px] rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15 resize-y disabled:bg-slate-50";

const priorityTone = (priority) => {
  const value = String(priority || "").toUpperCase();
  if (value === "HIGH") return "text-red-600";
  if (value === "MEDIUM") return "text-orange-500";
  if (value === "LOW") return "text-slate-500";
  return "text-slate-600";
};

const FormField = ({ label, required, error, touched, className = "", children }) => (
  <div className={className}>
    <label className={FILTER_LABEL_CLASS}>
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </label>
    {children}
    {error && touched ? <p className="mt-1 text-xs font-medium text-red-500">{error}</p> : null}
  </div>
);

const FormSection = ({ title, description, children }) => (
  <section className="rounded-xl border border-slate-200 bg-slate-50/50 p-5">
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {description ? <p className="mt-1 text-xs text-slate-500">{description}</p> : null}
    </div>
    <div className="grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">{children}</div>
  </section>
);

const FormSkeleton = () => (
  <div className="space-y-5 p-6">
    {Array.from({ length: 3 }).map((_, index) => (
      <div key={index} className="rounded-xl border border-slate-200 bg-slate-50/50 p-5">
        <div className="mb-4 h-4 w-32 animate-pulse rounded bg-slate-100" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((__, cellIndex) => (
            <div key={cellIndex} className="h-11 animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const buildInitialValues = (formColumns) => {
  const values = {
    accountNo: "",
    mobileNumbers: [""],
    priority: TicketPriority.MEDIUM,
    remarks: "",
    assignedTo: "",
    targetOutstanding: "0.000",
    targetPaymentDate: "",
  };
  formColumns.forEach((column) => {
    if (!PREDEFINED_TICKET_FIELD_KEYS.has(column.fieldKey)) {
      values[column.fieldKey] = "";
    }
  });
  return values;
};

const buildValidationSchema = (formColumns, t) => {
  const shape = {};
  formColumns.forEach((column) => {
    if (column.fieldKey === "mobileNumber") {
      let mobileSchema = Yup.array().of(Yup.string());
      if (column.mandatory) {
        mobileSchema = mobileSchema
          .test("at-least-one-mobile", t("ccMobileNumberRequired"), (numbers) =>
            hasValidMobileNumbers(numbers)
          )
          .test("valid-mobile-format", t("ccMobileNumberInvalid"), (numbers) => {
            if (!Array.isArray(numbers)) return false;
            return numbers.every((value) => !value || validatePhoneNumber(value) === null);
          })
          .test("unique-mobile-numbers", t("ccMobileNumberDuplicate"), (numbers) => {
            if (!Array.isArray(numbers)) return true;
            return findDuplicatePhoneIndexes(numbers).size === 0;
          });
      }
      shape.mobileNumbers = mobileSchema;
      return;
    }
    if (!column.mandatory) return;
    if (column.fieldKey === "priority") {
      shape.priority = Yup.string().oneOf(Object.values(TicketPriority)).required(t("ccPriorityRequired"));
      return;
    }
    if (column.dataType === "number") {
      shape[column.fieldKey] = Yup.number()
        .typeError(t("ccInvalidNumber"))
        .required(`${column.displayLabel} is required`);
      return;
    }
    shape[column.fieldKey] = Yup.string().trim().required(`${column.displayLabel} is required`);
  });
  if (!shape.accountNo) {
    shape.accountNo = Yup.string().trim().required(t("ccAccountNumberRequired"));
  }
  if (!shape.priority) {
    shape.priority = Yup.string().oneOf(Object.values(TicketPriority)).required(t("ccPriorityRequired"));
  }
  return Yup.object(shape);
};

const groupColumnsBySection = (columns) => {
  const used = new Set();
  const groups = {
    account: [],
    contact: [],
    assignment: [],
    payment: [],
    notes: [],
    additional: [],
  };

  columns.forEach((column) => {
    if (SECTION_KEYS.account.has(column.fieldKey)) {
      groups.account.push(column);
      used.add(column.fieldKey);
    } else if (SECTION_KEYS.contact.has(column.fieldKey)) {
      groups.contact.push(column);
      used.add(column.fieldKey);
    } else if (SECTION_KEYS.assignment.has(column.fieldKey)) {
      groups.assignment.push(column);
      used.add(column.fieldKey);
    } else if (SECTION_KEYS.payment.has(column.fieldKey)) {
      groups.payment.push(column);
      used.add(column.fieldKey);
    } else if (SECTION_KEYS.notes.has(column.fieldKey)) {
      groups.notes.push(column);
      used.add(column.fieldKey);
    }
  });

  columns.forEach((column) => {
    if (!used.has(column.fieldKey)) {
      groups.additional.push(column);
    }
  });

  return groups;
};

const CallCenterAgentWorkspacePage = () => {
  const t = useCallCenterT();
  const ticketsApi = useTickets();

  const [columnConfig, setColumnConfig] = useState([]);
  const [officeUsers, setOfficeUsers] = useState([]);
  const [officeUsersLoading, setOfficeUsersLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [createdTicket, setCreatedTicket] = useState(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const pendingClearRef = useRef(null);

  const formColumns = useMemo(() => getFormColumns(columnConfig), [columnConfig]);
  const orderedFormColumns = useMemo(() => {
    const byKey = new Map(formColumns.map((column) => [column.fieldKey, column]));
    const used = new Set();
    const ordered = [];
    FORM_FIELD_ORDER.forEach((key) => {
      if (byKey.has(key)) {
        ordered.push(byKey.get(key));
        used.add(key);
      }
    });
    formColumns.forEach((column) => {
      if (!used.has(column.fieldKey)) ordered.push(column);
    });
    return ordered;
  }, [formColumns]);

  const sectionGroups = useMemo(() => groupColumnsBySection(orderedFormColumns), [orderedFormColumns]);
  const requiredFields = useMemo(
    () => formColumns.filter((column) => column.mandatory).map((column) => column.displayLabel || column.fieldKey),
    [formColumns]
  );
  const initialTicket = useMemo(() => buildInitialValues(formColumns), [formColumns]);
  const ticketSchema = useMemo(() => buildValidationSchema(formColumns, t), [formColumns, t]);

  const loadWorkspace = useCallback(async () => {
    setPageLoading(true);
    setPageError("");
    try {
      setOfficeUsersLoading(true);
      const init = await getAgentWorkspaceInit();
      setOfficeUsers(Array.isArray(init?.officeUsers) ? init.officeUsers : []);
      setColumnConfig(Array.isArray(init?.columns) ? init.columns : []);
    } catch (error) {
      setPageError(error.message || t("ccToastLoadColumnConfigFailed"));
    } finally {
      setPageLoading(false);
      setOfficeUsersLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const handleCreateTicket = async (values, { resetForm }) => {
    setCreatingTicket(true);
    setCreatedTicket(null);
    try {
      const customFields = {};
      formColumns.forEach((column) => {
        if (column.fieldKey === "mobileNumber") {
          const joinedMobile = joinMobileNumberList(values.mobileNumbers);
          if (joinedMobile) customFields.mobileNumber = joinedMobile;
          return;
        }
        if (
          PREDEFINED_TICKET_FIELD_KEYS.has(column.fieldKey) &&
          !PREDEFINED_CUSTOM_FIELD_KEYS.has(column.fieldKey)
        ) {
          return;
        }
        const value = values[column.fieldKey];
        if (value !== "" && value != null) {
          customFields[column.fieldKey] = String(value);
        }
      });

      const payload = {
        accountNo: values.accountNo,
        priority: values.priority,
        remarks: values.remarks || undefined,
        assignedTo: values.assignedTo ? Number(values.assignedTo) : undefined,
        targetOutstanding:
          values.targetOutstanding !== "" && values.targetOutstanding != null
            ? Number(values.targetOutstanding)
            : undefined,
        targetPaymentDate: values.targetPaymentDate || undefined,
        customFields: Object.keys(customFields).length ? customFields : undefined,
      };

      const created = await ticketsApi.create(payload);
      toast.success(t("ccToastTicketCreated"));
      setCreatedTicket({
        id: created?.id,
        accountNo: values.accountNo,
      });
      resetForm({ values: initialTicket });
    } catch (error) {
      toast.error(error.message || t("ccToastCreateTicketFailed"));
    } finally {
      setCreatingTicket(false);
    }
  };

  const renderDynamicField = (column, errors, touched) => {
    const label = column.displayLabel || column.fieldKey;
    const fieldError = errors[column.fieldKey];
    const fieldTouched = touched[column.fieldKey];
    const errorClass = fieldError && fieldTouched ? "border-red-500 focus:border-red-500 focus:ring-red-500/15" : "";

    if (column.fieldKey === "mobileNumber") {
      const mobileError = typeof errors.mobileNumbers === "string" ? errors.mobileNumbers : "";
      return (
        <FormField
          key={column.fieldKey}
          label={label}
          required={column.mandatory}
          error={mobileError}
          touched={Boolean(mobileError)}
          className="md:col-span-2"
        >
          <FieldArray name="mobileNumbers">
            {({ form }) => (
              <PhoneNumberFieldArray
                values={form.values.mobileNumbers}
                onChange={(next) => form.setFieldValue("mobileNumbers", next)}
                hasError={Boolean(mobileError)}
                addLabel={t("ccAddMobileNumber")}
                removeLabel={t("ccRemoveMobileNumber")}
                placeholder={t("ccMobileNumberPlaceholder")}
                hint={t("ccMobileNumberHint")}
                fieldLabelPrefix={t("ccMobileNumber")}
                showFieldLabels
                t={t}
              />
            )}
          </FieldArray>
        </FormField>
      );
    }

    if (column.fieldKey === "priority") {
      return (
        <FormField
          key={column.fieldKey}
          label={label}
          required={column.mandatory}
          error={fieldError}
          touched={fieldTouched}
        >
          <Field as="select" name="priority" className={FILTER_FIELD_CLASS}>
            {Object.values(TicketPriority).map((priority) => (
              <option key={priority} value={priority} className={priorityTone(priority)}>
                {priority}
              </option>
            ))}
          </Field>
        </FormField>
      );
    }

    if (column.fieldKey === "assignedTo") {
      return (
        <FormField
          key={column.fieldKey}
          label={label}
          required={column.mandatory}
          error={fieldError}
          touched={fieldTouched}
        >
          <Field as="select" name="assignedTo" className={FILTER_FIELD_CLASS} disabled={officeUsersLoading}>
            <option value="">{officeUsersLoading ? t("ccLoadingUsers") : t("ccSelectUserOptional")}</option>
            {officeUsers.map((user) => (
              <option key={user.userId} value={String(user.userId)}>
                {user.name || user.email || `${t("ccUserFallback")} ${user.userId}`} ({user.userId})
                {user.agentExtension ? ` • Ext ${user.agentExtension}` : ""}
              </option>
            ))}
          </Field>
        </FormField>
      );
    }

    if (column.fieldKey === "remarks") {
      return (
        <FormField
          key={column.fieldKey}
          label={label}
          required={column.mandatory}
          error={fieldError}
          touched={fieldTouched}
          className="md:col-span-2"
        >
          <Field
            as="textarea"
            name="remarks"
            rows="3"
            placeholder={t("ccRemarksPlaceholder")}
            className={`${TEXTAREA_CLASS} ${errorClass}`}
          />
        </FormField>
      );
    }

    if (column.fieldKey === "accountNo") {
      return (
        <FormField
          key={column.fieldKey}
          label={label}
          required={column.mandatory}
          error={fieldError}
          touched={fieldTouched}
          className="md:col-span-2"
        >
          <Field
            name="accountNo"
            type="text"
            placeholder={t("ccAccountNoExample")}
            className={`${FILTER_FIELD_CLASS} ${errorClass}`}
          />
        </FormField>
      );
    }

    const inputType = column.dataType === "number" ? "number" : column.dataType === "date" ? "date" : "text";
    return (
      <FormField
        key={column.fieldKey}
        label={label}
        required={column.mandatory}
        error={fieldError}
        touched={fieldTouched}
        className={column.fieldKey === "remarks" ? "md:col-span-2" : ""}
      >
        <Field
          name={column.fieldKey}
          type={inputType}
          step={inputType === "number" ? "any" : undefined}
          placeholder={column.fieldKey === "targetOutstanding" ? "0.000" : undefined}
          className={`${inputType === "number" || inputType === "date" ? FILTER_FIELD_CLASS : FILTER_FIELD_CLASS} ${errorClass}`}
        />
      </FormField>
    );
  };

  const renderSection = (title, description, columns, errors, touched) => {
    if (!columns.length) return null;
    return (
      <FormSection title={title} description={description}>
        {columns.map((column) => renderDynamicField(column, errors, touched))}
      </FormSection>
    );
  };

  return (
    <div className={LISTING_PAGE_CLASS}>
      <div className={LISTING_CONTAINER_CLASS}>
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-[28px] font-bold tracking-tight text-[#111827]">{t("ccAgentWorkspaceTitle")}</h1>
            <p className="mt-1 text-sm text-[#6b7280]">{t("ccAgentWorkspaceSubtitle")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/call-center/tickets"
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <FiList className="h-4 w-4" />
              {t("ccTicketEntryViewTickets")}
            </Link>
            <Link
              to="/call-center/ticket-bulk-upload"
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <FiUpload className="h-4 w-4" />
              {t("ccMenuTicketBulkUpload")}
            </Link>
            <Link
              to="/call-center/ticket-settings?tab=columns"
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <FiSettings className="h-4 w-4" />
              {t("ccMenuTicketSettingsMerged")}
            </Link>
          </div>
        </header>

        {pageError && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>{pageError}</span>
            <button
              type="button"
              onClick={loadWorkspace}
              className="font-semibold text-red-800 underline-offset-2 hover:underline"
            >
              {t("ccRefresh")}
            </button>
          </div>
        )}

        {createdTicket?.id && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <div className="flex items-center gap-2">
              <FiCheckCircle className="h-4 w-4 shrink-0" />
              <span>{t("ccToastTicketCreated")}</span>
            </div>
            <Link
              to={`/call-center/tickets?ticketId=${createdTicket.id}${
                createdTicket.accountNo ? `&accountNo=${encodeURIComponent(createdTicket.accountNo)}` : ""
              }`}
              className="font-semibold text-emerald-900 underline-offset-2 hover:underline"
            >
              {t("ccViewTicketInList")}
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
          <div className="xl:col-span-8">
            <div className={LISTING_CARD_CLASS}>
              <div className="border-b border-slate-100 px-6 py-4">
                <h2 className="text-base font-semibold text-[#111827]">{t("ccManualTicketEntry")}</h2>
                <p className="mt-1 text-xs text-slate-500">{t("ccTicketEntryFormHint")}</p>
              </div>

              {pageLoading ? (
                <FormSkeleton />
              ) : (
                <Formik
                  initialValues={initialTicket}
                  enableReinitialize
                  validationSchema={ticketSchema}
                  onSubmit={handleCreateTicket}
                >
                  {({ errors, touched, resetForm, dirty }) => (
                    <Form>
                      <div className="space-y-5 p-6">
                        {renderSection(
                          t("ccTicketEntrySectionAccount"),
                          t("ccTicketEntrySectionAccountHint"),
                          sectionGroups.account,
                          errors,
                          touched
                        )}
                        {renderSection(
                          t("ccTicketEntrySectionContact"),
                          t("ccTicketEntrySectionContactHint"),
                          sectionGroups.contact,
                          errors,
                          touched
                        )}
                        {renderSection(
                          t("ccTicketEntrySectionAssignment"),
                          null,
                          sectionGroups.assignment,
                          errors,
                          touched
                        )}
                        {renderSection(
                          t("ccTicketEntrySectionPayment"),
                          null,
                          sectionGroups.payment,
                          errors,
                          touched
                        )}
                        {renderSection(
                          t("ccTicketEntrySectionNotes"),
                          t("ccTicketEntrySectionNotesHint"),
                          sectionGroups.notes,
                          errors,
                          touched
                        )}
                        {renderSection(
                          t("ccTicketEntrySectionAdditional"),
                          null,
                          sectionGroups.additional,
                          errors,
                          touched
                        )}
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60 px-6 py-4">
                        <p className="text-xs text-slate-500">
                          <span className="text-red-500">*</span> {t("ccTicketEntryRequiredHint")}
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const clearForm = () => {
                                resetForm({ values: initialTicket });
                                setCreatedTicket(null);
                              };
                              if (dirty) {
                                pendingClearRef.current = clearForm;
                                setClearConfirmOpen(true);
                                return;
                              }
                              clearForm();
                            }}
                            disabled={creatingTicket}
                            className="h-10 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                          >
                            {t("ccClear")}
                          </button>
                          <button
                            type="submit"
                            disabled={creatingTicket || pageLoading}
                            className={`${LISTING_PRIMARY_BUTTON_CLASS} disabled:cursor-not-allowed disabled:opacity-70`}
                          >
                            {creatingTicket ? t("ccProcessing") : t("ccCreateTicket")}
                          </button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              )}
            </div>
          </div>

          <aside className="space-y-5 xl:col-span-4">
            {requiredFields.length > 0 && (
              <div className={LISTING_FILTER_CARD_CLASS}>
                <h3 className="mb-3 text-sm font-semibold text-slate-900">{t("ccTicketEntryRequiredFields")}</h3>
                <ul className="space-y-2">
                  {requiredFields.map((field) => (
                    <li key={field} className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#2B7FFF]" />
                      {field}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
              <div className="flex gap-3">
                <FiAlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div>
                  <h3 className="text-sm font-semibold text-amber-900">{t("ccTicketEntryAccountPlanTitle")}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-amber-800/90">{t("ccTicketEntryAccountPlanNote")}</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-[#F3E4CF] bg-[#FBF3E8] p-5">
              <div className="flex gap-3">
                <FiStar className="mt-0.5 h-5 w-5 shrink-0 fill-amber-400 text-amber-400" />
                <div>
                  <h3 className="text-sm font-semibold text-amber-800">{t("ccAgentTipTitle")}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-amber-800/80">{t("ccAgentTipBody")}</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <ConfirmDialog
        open={clearConfirmOpen}
        title={t("ccConfirmDialogTitle")}
        message={t("ccTicketEntryClearConfirm")}
        cancelLabel={t("ccCancel")}
        confirmLabel={t("ccConfirmDialogConfirm")}
        processingLabel={t("ccProcessing")}
        variant="warning"
        onConfirm={() => {
          pendingClearRef.current?.();
          pendingClearRef.current = null;
          setClearConfirmOpen(false);
        }}
        onClose={() => {
          pendingClearRef.current = null;
          setClearConfirmOpen(false);
        }}
      />
    </div>
  );
};

export default CallCenterAgentWorkspacePage;
